'use client';

import type { CardProps } from '@packages/shared/src/ui/Card/Card';
import type { CollapseProps } from '@packages/shared/src/components/Collapse/Collapse';
import { useIntl } from 'react-intl';
import { Card, CardFooter } from '@packages/shared/src/ui/Card/Card';
import { Collapse } from '@packages/shared/src/components/Collapse/Collapse';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import type { ReactNode } from 'react';
import { startTransition, useState } from 'react';
import { Overlay } from './Overlay';
import { collapsibleCardMessages } from './messages';

const expandLabelId = 'collapsibleCard-expand';

export type CollapsibleCardProps = {
  /** Optional children react node */
  children?: ReactNode;
  /**
   * Children for the collapsed content.
   */
  collapseChildren: ReactNode;

  /**
   * The width (horizontal) or height (vertical) of the container when collapsed.
   * @default '0px'
   */
  collapsedSize?: CollapseProps['collapsedSize'];
  /**
   * Text that can be clicked to extend/collapse the card.
   */
  cardActionText: {
    /**
     * Text that should be shown to extend card
     */
    expandText: string;
    /**
     * Text that should be shown to collapse card
     */
    collapseText: string;
  };
} & CardProps;

/**
 * This Component is an extension of the Card component from MUI: https://mui.com/material-ui/react-card/
 *
 * In addition to the default Card, you can add collapseChildren, which can be expanded on click.
 * */
export const CollapsibleCard = ({
  cardActionText,
  children,
  collapseChildren,
  collapsedSize,
  ...rest
}: CollapsibleCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const { formatMessage } = useIntl();

  const handleExpandClick = () => startTransition(() => setExpanded((prev) => !prev));

  return (
    <Card {...rest}>
      {children}
      <Collapse collapsedSize={collapsedSize} in={expanded} sx={{ position: 'relative' }}>
        {!collapsedSize && <Divider />}
        {collapsedSize && <Overlay expanded={expanded} />}
        {collapseChildren}
      </Collapse>
      <Divider />
      <CardFooter className="px-0">
        <button
          type="button"
          className="flex w-full items-center gap-1 py-1"
          onClick={() => handleExpandClick()}
          aria-label={formatMessage(
            expanded
              ? collapsibleCardMessages.collapsibleCardCollapsedAria
              : collapsibleCardMessages.collapsibleCardExpendedAria,
          )}
        >
          <p className="text-size-body2 pl-2" id={expandLabelId}>
            {/** If card is expanded then it shows the text to collapse the card and vice versa */}
            {expanded ? cardActionText.collapseText : cardActionText.expandText}
          </p>
          <span
            className={`icon-emp-arrow-medium-down bg-color-grey-dark h-4 w-4 ${expanded ? 'rotate-180' : ''}`}
            aria-labelledby={expandLabelId}
          />
        </button>
      </CardFooter>
    </Card>
  );
};
